import { useEffect, useRef, useState } from 'react';
import { Feature, LineString, Position } from 'geojson';

export interface UseRouteDirectionsResult {
  feature: Feature<LineString> | null;
  loading: boolean;
  error: string | null;
}

// Cache en memoria por clave de ruta (hash de coords + routeKey)
const routeCache: Record<string, Feature<LineString>> = {};

const buildCacheKey = (coordinates: Position[], routeKey: string | null) => {
  const coordsPart = coordinates
    .map((c) => `${c[0].toFixed(6)},${c[1].toFixed(6)}`)
    .join('|');
  return `${routeKey ?? 'no-route-key'}::${coordsPart}`;
};

/**
 * Llama al backend (VITE_LASTMILE_URL/valhalla/route-chunked),
 * enviando el payload en formato Valhalla:
 *
 * {
 *   locations: [{ lon, lat }, ...],
 *   costing: "auto",
 *   directions_options: { units: "km" }
 * }
 *
 * y espera un Feature<LineString> como el que pegaste.
 */
export const useRouteDirections = (
  coordinates: Position[] | null,
  accessToken: string | null, // ya no se usa, se mantiene por compatibilidad
  routeKey: string | null,
): UseRouteDirectionsResult => {
  const [feature, setFeature] = useState<Feature<LineString> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!coordinates || coordinates.length < 2 || !routeKey) {
      setFeature(null);
      setLoading(false);
      return;
    }

    const cacheKey = buildCacheKey(coordinates, routeKey);

    // Ya hicimos esta llamada y tenemos feature → no repetir
    if (lastKeyRef.current === cacheKey && feature) {
      return;
    }

    // Cache global
    if (routeCache[cacheKey]) {
      lastKeyRef.current = cacheKey;
      setFeature(routeCache[cacheKey]);
      setLoading(false);
      setError(null);
      return;
    }

    const fetchRoute = async () => {
      try {
        lastKeyRef.current = cacheKey;
        setLoading(true);
        setError(null);

        const baseUrl = (import.meta as any).env.VITE_LASTMILE_URL as
          | string
          | undefined;

        if (!baseUrl) {
          throw new Error('Missing VITE_LASTMILE_URL environment variable');
        }

        // Payload en formato Valhalla original:
        const locations = coordinates.map((c) => ({
          lon: c[0],
          lat: c[1],
        }));

        const body = {
          locations,
          costing: 'auto',
          directions_options: {
            units: 'km',
          },
        };

        const res = await fetch(`${baseUrl}/valhalla/route-chunked`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        });

        if (!res.ok) {
          const text = await res.text();
          throw new Error(
            `Backend valhalla/route-chunked error: ${res.status} - ${text}`,
          );
        }

        const data = await res.json();
        const feat = data as Feature<LineString>;

        if (
          !feat ||
          feat.type !== 'Feature' ||
          !feat.geometry ||
          feat.geometry.type !== 'LineString' ||
          !Array.isArray(feat.geometry.coordinates) ||
          feat.geometry.coordinates.length < 2
        ) {
          throw new Error(
            'Invalid LineString feature returned from valhalla/route-chunked',
          );
        }

        routeCache[cacheKey] = feat;
        setFeature(feat);
      } catch (e: any) {
        setError(e?.message ?? 'Error fetching route from backend');
        setFeature(null);
      } finally {
        setLoading(false);
      }
    };

    fetchRoute();
  }, [coordinates, accessToken, routeKey, feature]);

  return { feature, loading, error };
};
